import { c as _c } from "react/compiler-runtime";
import { skipToken, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { authClient } from "@/clients/authClient";
import { useIsAdmin } from "@/lib/hooks/useIsAdmin";
import { parseRouteId } from "@/lib/utils/routeId";
import { trpc } from "@/router";

// ─── Feature type grouping ──────────────────────────────────────────────────

const FEATURE_TYPE_ORDER = {
  Huvudentré: 0,
  Sidoentré: 1,
  Parkering: 2,
  Toalett: 3,
  Hiss: 4,
  Service: 5,
  Nödutgång: 6
};
const FEATURE_TYPE_GROUPS = {
  Huvudentré: "Entréer",
  Sidoentré: "Entréer",
  Parkering: "Parkering",
  Toalett: "Toaletter",
  Hiss: "Hissar",
  Service: "Service",
  Nödutgång: "Nödutgångar"
};
export function groupFeatures(features) {
  const groups = new Map();
  for (const f of features) {
    const typeName = f.featureType?.name ?? "Övrigt";
    const groupName = FEATURE_TYPE_GROUPS[typeName] ?? typeName;
    if (!groups.has(groupName)) groups.set(groupName, []);
    groups.get(groupName).push(f);
  }
  return Array.from(groups.entries()).sort(([, a], [, b]) => {
    const aOrder = FEATURE_TYPE_ORDER[a[0]?.featureType?.name] ?? 99;
    const bOrder = FEATURE_TYPE_ORDER[b[0]?.featureType?.name] ?? 99;
    return aOrder - bOrder;
  }).map(([name, features]) => ({
    name,
    features: features.sort((a, b) => (FEATURE_TYPE_ORDER[a.featureType?.name] ?? 99) - (FEATURE_TYPE_ORDER[b.featureType?.name] ?? 99))
  }));
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function usePlatsDetail() {
  const $ = _c(26);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      from: "/_app/plats/$id"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    id
  } = useParams(t0);
  const placeId = parseRouteId(id);
  const navigate = useNavigate();
  const {
    data: session
  } = authClient.useSession();
  const currentUserId = session?.user?.id;
  const isAdmin = useIsAdmin();
  const {
    data: place,
    isLoading
  } = useQuery(trpc.places.byId.queryOptions(placeId ? {
    id: placeId
  } : skipToken));
  const {
    data: fullPlace
  } = useQuery(trpc.places.byIdFull.queryOptions(placeId ? {
    id: placeId
  } : skipToken));
  let t1;
  let visibleFeatures;
  if ($[1] !== currentUserId || $[2] !== fullPlace?.features || $[3] !== isAdmin) {
    const allFeatures = fullPlace?.features ?? [];
    visibleFeatures = isAdmin ? allFeatures : allFeatures.filter(e => e.status === "approved" || e.status === "pending" && e.createdBy === currentUserId);
    t1 = visibleFeatures.filter(_temp).map(_temp2);
    $[1] = currentUserId;
    $[2] = fullPlace?.features;
    $[3] = isAdmin;
    $[4] = t1;
    $[5] = visibleFeatures;
  } else {
    t1 = $[4];
    visibleFeatures = $[5];
  }
  const featurePins = t1;
  let t2;
  if ($[6] !== visibleFeatures) {
    t2 = groupFeatures(visibleFeatures);
    $[6] = visibleFeatures;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const grouped = t2;
  let t3;
  if ($[8] !== placeId) {
    t3 = placeId ? {
      plats: placeId
    } : {};
    $[8] = placeId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const platsSearch = t3;
  let t4;
  if ($[10] !== navigate || $[11] !== platsSearch) {
    t4 = () => navigate({
      to: "/karta",
      search: platsSearch
    });
    $[10] = navigate;
    $[11] = platsSearch;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const navigateToKarta = t4;
  let t5;
  if ($[13] !== navigate || $[14] !== place?.name || $[15] !== platsSearch) {
    t5 = () => navigate({
      to: "/bidra",
      search: {
        ...platsSearch,
        platsNamn: place?.name
      }
    });
    $[13] = navigate;
    $[14] = place?.name;
    $[15] = platsSearch;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const navigateToBidra = t5;
  let t6;
  if ($[17] !== featurePins || $[18] !== grouped || $[19] !== isAdmin || $[20] !== isLoading || $[21] !== navigateToBidra || $[22] !== navigateToKarta || $[23] !== place || $[24] !== visibleFeatures) {
    t6 = {
      place,
      isLoading,
      isAdmin,
      visibleFeatures,
      featurePins,
      grouped,
      navigateToKarta,
      navigateToBidra
    };
    $[17] = featurePins;
    $[18] = grouped;
    $[19] = isAdmin;
    $[20] = isLoading;
    $[21] = navigateToBidra;
    $[22] = navigateToKarta;
    $[23] = place;
    $[24] = visibleFeatures;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
}
function _temp2(e_1) {
  return {
    id: e_1.id,
    label: e_1.featureType?.name ?? "Entr\xE9",
    latitude: e_1.location.latitude,
    longitude: e_1.location.longitude,
    status: e_1.status
  };
}
function _temp(e_0) {
  return e_0.location;
}